import { useEffect, useRef, useState } from 'preact/hooks';

export type AsyncState<T> =
  | { status: 'loading' }
  | { status: 'ok'; data: T; refreshing: boolean }
  | { status: 'error'; error: unknown };

/**
 * Fetch-on-mount + stale-while-revalidate helper.
 *
 * On first mount: `status: 'loading'` until the fetch resolves.
 * When `deps` change and a previous fetch had succeeded: the state stays
 *   at `status: 'ok'` with the OLD `data` still visible, but `refreshing`
 *   flips to `true`. When the new fetch finishes, `data` is replaced and
 *   `refreshing` goes back to `false`. That way the UI never flashes
 *   "Lade…" over already-visible content — the operator sees a small
 *   opt-in progress indicator (or nothing) instead of a jarring reset.
 *
 * On error while we had good data: we KEEP the good data visible and
 *   clear the refreshing flag. Silent recovery is the SwR default; if a
 *   caller needs the last error, that's a follow-up feature.
 *
 * Cancels via AbortController on unmount and when `deps` change.
 */
export function useAsync<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
): AsyncState<T> {
  const [state, setState] = useState<AsyncState<T>>({ status: 'loading' });
  // Track "have we ever had ok data" via a ref so setState in the effect
  // can look at the latest value without needing state in the effect deps.
  const lastGoodRef = useRef<T | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    // If we already have good data, transition to refreshing instead of
    // wiping back to a loading placeholder.
    if (lastGoodRef.current !== null) {
      setState({ status: 'ok', data: lastGoodRef.current, refreshing: true });
    } else {
      setState({ status: 'loading' });
    }
    fetcher(controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        lastGoodRef.current = data;
        setState({ status: 'ok', data, refreshing: false });
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        if (lastGoodRef.current !== null) {
          // Silent recovery: keep the previously good data visible.
          setState({ status: 'ok', data: lastGoodRef.current, refreshing: false });
        } else {
          setState({ status: 'error', error });
        }
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return state;
}
